import {
  RobotoDomainException,
  RobotoUnauthorizedException,
  RobotoNotFoundException,
} from "@/shared/http";

import { isBoardDraft, type Board } from "./BoardRecord";

export interface BoardViewer {
  userId: string | undefined;
  orgId: string | undefined;
  isAdmin: boolean;
}

/**
 * What `viewer` may do with a board.
 *
 * A draft is kept in the browser of the person who made it, so whoever has it
 * may edit and delete it, and it cannot be shared until it is saved.
 *
 * A stored board follows who owns it, by the rules the platform applies:
 *
 * - Only a signed-in person viewing the board's own organization gets any right.
 * - Every such person may edit a shared board.
 * - The owner and the organization's admins may edit, share and delete a
 *   personal board. The organization owns a shared board, so only its admins
 *   may delete one.
 */
export function boardPermissions(board: Board, viewer: BoardViewer) {
  if (isBoardDraft(board)) {
    return {
      draft: true,
      shared: false,
      canEdit: true,
      canShare: false,
      canDelete: true,
    };
  }
  const shared = board.owner_principal_id === `org:${board.org_id}`;
  const sameOrg = viewer.orgId === board.org_id && viewer.userId !== undefined;
  const owner = board.owner_principal_id === `user:${viewer.userId}`;
  const ownerOrOrgAdmin = sameOrg && (owner || viewer.isAdmin);
  return {
    draft: false,
    shared,
    canEdit: ownerOrOrgAdmin || (sameOrg && shared),
    canShare: ownerOrOrgAdmin && !shared,
    canDelete: ownerOrOrgAdmin,
  };
}

/**
 * Whether a failed board request means the platform refused this person: a
 * 401, 403 or 404 answer, as its exception class or, where the http layer could
 * not pick one, as a `RobotoDomainException` carrying the status code. A
 * network failure, a server error or a revision conflict says nothing about
 * access, so writes stay enabled after one.
 */
export function isBoardAccessDenied(error: unknown): boolean {
  if (
    error instanceof RobotoUnauthorizedException ||
    error instanceof RobotoNotFoundException
  ) {
    return true;
  }
  return (
    error instanceof RobotoDomainException &&
    ["401", "403", "404"].includes(error.errorCode)
  );
}
